import { supabase } from "@/lib/supabase";

/**
 * Removes invisible Unicode characters that can be inserted by keyboards.
 * These include zero-width spaces, joiners, and other invisible formatting characters
 * that would otherwise cause username validation to fail.
 */
export function sanitizeUsername(username: string): string {
  /* eslint-disable no-misleading-character-class, no-control-regex */
  return username
    .replace(/[\u200B-\u200F\uFEFF\u00AD\u2060-\u2064\u034F]/g, "") // Zero-width chars
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, "") // Unicode spaces
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "") // Control characters
    .replace(/[\u2028\u2029]/g, "") // Line/paragraph separators
    .replace(/\s/g, ""); // Any remaining whitespace
  /* eslint-enable no-misleading-character-class, no-control-regex */
}

/**
 * Validates a username according to the following rules:
 * - Must contain only letters, numbers, hyphens, and underscores
 * - Must be between 4 and 30 characters long
 * - Must be unique (case-insensitive, unless it belongs to the current user)
 *
 * @param username The username to validate
 * @param currentUserId The ID of the current user (to allow them to keep their existing username)
 * @returns Error message if validation fails, null if valid
 */
export async function validateUsername(
  username: string,
  currentUserId?: string
): Promise<string | null> {
  const sanitized = sanitizeUsername(username);

  // Check for allowed characters (alphanumeric, hyphen, underscore)
  const validCharactersRegex = /^[a-zA-Z0-9_-]+$/;
  if (sanitized.length > 0 && !validCharactersRegex.test(sanitized)) {
    return "Use letters, numbers, or dashes only";
  }

  // Check length constraints
  if (sanitized.length > 0 && sanitized.length < 4) {
    return "At least four characters";
  }
  if (sanitized.length > 30) {
    return "30 characters max";
  }

  // Check against reserved usernames (case-insensitive)
  try {
    const { data: reservedData, error: reservedError } = await supabase
      .from("reserved_usernames")
      .select("username")
      .ilike("username", sanitized)
      .limit(1);

    if (reservedError) {
      console.error("Reserved username check failed:", reservedError);
    } else if (reservedData && reservedData.length > 0) {
      return "This username is reserved";
    }
  } catch (error) {
    console.error("Reserved username validation error:", error);
  }

  // Check uniqueness (case-insensitive)
  try {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("id")
      .ilike("username", sanitized)
      .eq("is_active", true)
      .limit(1);

    if (error) {
      console.error("Username uniqueness check failed:", error);
      return null;
    }

    if (data && data.length > 0 && data[0].id !== currentUserId) {
      return "That one is already taken";
    }
  } catch (error) {
    console.error("Username validation error:", error);
    return null;
  }

  return null;
}
